// ------------------------------------------------------------------------------
// <copyright file="manualQaAdminExport.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

function escapeCsvValue(value) {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function toCsv(headers, rows) {
  return [
    headers.join(','),
    ...rows.map(row => headers.map(header => escapeCsvValue(row[header])).join(',')),
  ].join('\n');
}

/**
 * Builds a submitted-run report CSV for Test Admin offline review.
 *
 * @param {Array<object>} reports - Completion report documents.
 * @returns {string} CSV text.
 */
export function buildManualQaReportsCsv(reports) {
  const headers = [
    'reportId',
    'runId',
    'testerUserId',
    'bugCount',
    'testerReportedHours',
    'evidenceCount',
    'submittedAtUtc',
  ];
  const rows = (reports || []).map(report => ({
    reportId: report.id || '',
    runId: report.runId || '',
    testerUserId: report.testerUserId || '',
    bugCount: report.bugCount || 0,
    testerReportedHours: report.testerReportedHours || 0,
    evidenceCount: report.evidenceCount || '',
    submittedAtUtc: report.submittedAtUtc || '',
  }));

  return toCsv(headers, rows);
}

/**
 * Builds a payable-hours CSV for payroll review.
 *
 * @param {Array<object>} rows - Test Admin payable-hours table rows.
 * @returns {string} CSV text.
 */
export function buildManualQaHoursCsv(rows) {
  const headers = [
    'runId',
    'testerUserId',
    'tester',
    'systemHours',
    'elapsedHours',
    'testerReportedHours',
    'approvedHours',
    'status',
    'campaign',
  ];
  const csvRows = (rows || []).map(row => ({
    runId: row.runId || '',
    testerUserId: row.testerUserId || '',
    tester: row.tester || '',
    systemHours: row.active || 0,
    elapsedHours: row.elapsed || 0,
    testerReportedHours: row.claimed || 0,
    approvedHours: row.approved || 0,
    status: row.status || '',
    campaign: row.campaign || '',
  }));

  return toCsv(headers, csvRows);
}

/**
 * Downloads CSV text through the browser without sending data back to the server.
 *
 * @param {string} fileName - Download filename.
 * @param {string} csvText - CSV payload.
 */
export function downloadManualQaCsv(fileName, csvText) {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
